/* eslint-disable no-func-assign */
import { FC, useState } from "react";
import styles from "./CollectionsList.module.css";
import { CollectionDb } from "../../models/collection.db";
import { CollectionItemDb } from "../../models/collection-item.db";
import { CollectionOutfitDb } from "../../models/collection-outfit.db";
import { CollectionLinkDb } from "../../models/collectio-link.db";
import { InspirationDb } from "../../models/inspiration.db";
import { getDefaultFolderId } from "../../utils/collectionUtils";
import { ItemDb } from "../../models/item.db";
import { OutfitDb } from "../../models/outfit.db";
import ItemsList from "../ItemsList/ItemsList";
import OutfitsList from "../OutfitsList/OutfitsList";
import { useNavigate } from "react-router";
import InspirationsList from "../InspirationsList/InspirationsList";

enum CollectionTab {
  items,
  outfits,
}

interface CollectionsData {
  items: Array<ItemDb>;
  outfits: Array<OutfitDb>;
  collections: Array<CollectionDb>;
  collectionItems: Array<CollectionItemDb>;
  collectionOutfits: Array<CollectionOutfitDb>;
  collectionInspirations: Array<CollectionLinkDb>;
  inspirations: Array<InspirationDb>;
}

interface Props {
  userId: string;
  collectionId: string | null;
  data: CollectionsData;
  onShowModal: () => void;
  emptyString: string;
}

const colors = {
  "default-wishlist": "rgb(198, 188, 169)",
  "default-packing": "rgb(17, 52, 4)",
  "default-inspiration": "rgb(108, 97, 40)",
  "default-capsules": "rgb(242, 228, 141)",
  "": "rgb(255, 255, 255)",
};

const CollectionsList: FC<Props> = ({
  userId,
  collectionId,
  data,
  onShowModal,
  emptyString,
}) => {
  const navigate = useNavigate();
  const [collectionTab, setCollectionTab] = useState<CollectionTab>(
    CollectionTab.items
  );

  if (collectionId) {
    const collection = data.collections.find((c) => c.id === collectionId);

    if (!collection) {
      return (
        <div className={styles.listCards}>
          <div
            style={{
              width: "100%",
              height: "50vh",
              display: "flex",
              alignSelf: "center",
              justifySelf: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Can't find collection
          </div>
        </div>
      );
    }

    const folder = getDefaultFolderId(collection);
    //const isPacking = folder === "default-packing";
    //const isCapsule = folder === "default-capsules";
    const isWishList = folder === "default-wishlist";
    const isInspiration = folder === "default-inspiration";

    const hideButtons = isWishList || isInspiration;

    const showOutfits =
      collectionTab === CollectionTab.outfits && !isInspiration;
    const showItems =
      (isWishList || collectionTab === CollectionTab.items) && !isInspiration;
    const showInspirations = isInspiration;

    const itemsIds = new Set(
      data.collectionItems
        .filter((ci) => ci.collectionId === collection.id)
        .map((ci) => ci.itemId)
    );
    const items = data.items.filter((i) => itemsIds.has(i.id));
    // --
    const outfitsIds = new Set(
      data.collectionOutfits
        .filter((co) => co.collectionId === collection.id)
        .map((ci) => ci.outfitId)
    );
    const outfits = data.outfits.filter((i) => outfitsIds.has(i.id));
    //
    const inspirationsIds = new Set(
      data.collectionInspirations
        .filter((co) => co.collectionId === collection.id)
        .map((ci) => ci.targetId)
    );
    const inspirations = data.inspirations.filter((i) =>
      inspirationsIds.has(i.id)
    );

    return (
      <>
        <div
          style={{
            display: "flex",
            height: 40,
            flexDirection: "row",
            alignItems: "center",
            background: "rgba(245, 241, 229, 1)",
            borderTop: "0.5px solid #000",
            paddingLeft: 20,
            fontFamily: "Roobert",
            fontSize: 16,
            fontWeight: 800,
          }}
        >
          {collection.name}
        </div>
        {showItems ? (
          <ItemsList
            items={items}
            onShowModal={onShowModal}
            emptyString="There are no items for this collection."
          />
        ) : null}
        {showOutfits ? (
          <OutfitsList
            outfits={outfits}
            onShowModal={onShowModal}
            emptyString="There are no outfits for this collection."
          />
        ) : null}
        {showInspirations ? (
          <InspirationsList
            inspirations={inspirations}
            onShowModal={onShowModal}
            emptyString="There are no outfits for this collection."
          />
        ) : null}
        {hideButtons ? null : (
          <div
            style={{
              position: "fixed",
              bottom: 80,
              right: 30,
            }}
            className={styles.switchBtnsContainer}
          >
            <div
              onClick={() => {
                setCollectionTab(CollectionTab.items);
              }}
              className={[
                styles.switchBtn,
                collectionTab === CollectionTab.items ? styles.activeBtn : "",
              ]
                .filter((c) => c)
                .join(" ")}
            >
              ITEMS
            </div>
            <div
              onClick={() => {
                setCollectionTab(CollectionTab.outfits);
              }}
              className={[
                styles.switchBtn,
                collectionTab === CollectionTab.outfits ? styles.activeBtn : "",
              ]
                .filter((c) => c)
                .join(" ")}
            >
              OUTFITS
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.listCards}>
      {data.collections.length ? (
        data.collections.map((collection) => {
          const folder = getDefaultFolderId(collection);
          const isPacking = folder === "default-packing";
          const isCapsule = folder === "default-capsules";
          const isWishList = folder === "default-wishlist";
          const isInspiration = folder === "default-inspiration";
          // --
          const itemsIds = new Set(
            data.collectionItems
              .filter((ci) => ci.collectionId === collection.id)
              .map((ci) => ci.itemId)
          );
          const items = data.items.filter((i) => itemsIds.has(i.id));
          const itemsCount = items.length;
          // --
          const outfitsIds = new Set(
            data.collectionOutfits
              .filter((co) => co.collectionId === collection.id)
              .map((ci) => ci.outfitId)
          );
          const outfits = data.outfits.filter((i) => outfitsIds.has(i.id));
          const outfitsCount = outfits.length;
          // --
          const inspirationsIds = new Set(
            data.collectionInspirations
              .filter((ci) => ci.collectionId === collection.id)
              .map((ci) => ci.targetId)
          );
          const inspirations = data.inspirations.filter((i) =>
            inspirationsIds.has(i.id)
          );
          const inspirationsCount = inspirations.length;
          // --

          const color = colors[folder];

          const line2parts: string[] = [];
          if (isPacking || isCapsule || isWishList) {
            line2parts.push(`${itemsCount} items`);
          }
          if (isPacking || isCapsule) {
            line2parts.push(`${outfitsCount} outfits`);
          }
          if (isInspiration) {
            line2parts.push(`${inspirationsCount} inspirations`);
          }
          const line2 = line2parts.join(" | ");

          let urls: string[] = [];
          let isItems = false;
          if (isWishList || ((isPacking || isCapsule) && outfitsCount === 0)) {
            urls = items
              .map((i) => i.thumbnailImageURL ?? "")
              .filter((url) => url);
            isItems = true;
          }
          if ((isPacking || isCapsule) && outfitsCount !== 0) {
            urls = outfits.map((i) => i.previewUrl ?? "").filter((url) => url);
          }
          if (isInspiration) {
            urls = inspirations.map((i) => i.picUrl).filter((url) => url);
          }

          const slicedUrls = urls.slice(0, 10);

          return (
            <div
              key={collection.id}
              onClick={() => {
                navigate(`/user/${userId}/collections/${collection.id}`);
              }}
              style={{
                display: "flex",
                flexDirection: "row",
                height: 89,
                maxWidth: 800,
                background: "white",
                width: "100%",
                border: "0.5px solid #000",
                marginBottom: 10,
                cursor: "pointer",
              }}
            >
              <div style={{ width: 10, background: color }}></div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "end",
                  width: 146,
                  paddingLeft: 10,
                  paddingBottom: 5,
                  paddingTop: 5,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "end",
                  }}
                >
                  <div className={styles.collectionLine3}>
                    {folder
                      .replaceAll("default-", "")
                      .toLowerCase()
                      .replaceAll("wishlist", "wish")
                      .replaceAll("capsules", "capsule")
                      .toUpperCase()}
                  </div>
                  <div
                    style={{ width: 146 - 20 }}
                    className={styles.collectionName}
                  >
                    {collection.name}
                  </div>
                  <div className={styles.collectionLine2}>{line2}</div>
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  overflow: "hidden",
                }}
              >
                {slicedUrls.map((url) => {
                  return (
                    <div
                      key={[collection.id, url].join("-")}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 60,
                        height: "100%",
                        padding: isItems ? 5 : 0,
                        boxSizing: "border-box",
                        backgroundImage: isInspiration
                          ? `url(${url})`
                          : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }}
                    >
                      {isInspiration ? null : (
                        <img
                          key={url}
                          style={{
                            maxHeight: "95%",
                            maxWidth: "95%",
                            display: "block",
                            margin: "auto",
                          }}
                          src={url}
                          alt=""
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        <div
          style={{
            width: "100%",
            height: "50vh",
            display: "flex",
            alignSelf: "center",
            justifySelf: "center",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {emptyString}
        </div>
      )}
    </div>
  );
};
export default CollectionsList;
